import { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogContent, DialogDescription, DialogTitle, Dialog, DialogBody, DialogHeader } from "@/app/components/common/ui/dialog";
import { formatIsoDate } from '@/app/utils/Date';
import usePrice from '../../price/use-price';


const MembershipView = ({ open, onOpenChange, details }: any) => {
    const parentRef = useRef<any | null>(null);

    const PriceCell = ({ value }: { value: any }) => {
        const { price } = usePrice({ amount: value });
        return <span className="whitespace-nowrap">{price}</span>;
    };

const InfoCard = ({ titleId, data }: { titleId: string; data: Record<string, any> }) => {
    return (
        <div className="card min-w-full">
            <div className="card-header">
                <h3 className="card-title">
                    <FormattedMessage id={titleId} />
                </h3>
            </div>
            <div className="card-table scrollable-x-auto pb-3">
                <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5">
                    <table className="table align-middle text-sm text-gray-500">
                        <tbody>
                            {Object.entries(data || {}).map(([key, value]) => {
                                if (key === "add" || key === "edit") return null;

                                // Customized labels
                                let labelKey = key.toUpperCase();

                                if (titleId === "EXTENSION_LIST") {
                                    if (key === "max") labelKey = "EXTENSION_APPLICABLE";
                                    else if (key === "current") labelKey = "CREATED_EXTENSIONS";
                                    else if (key === "download") labelKey = "DOWNLOAD_EXTENSION";
                                } else {
                                    if (key === "max") labelKey = "LICENSE_APPLICABLE";
                                    else if (key === "current") labelKey = "CURRENT_LICENSES";
                                }

                                // Customized values
                                let displayValue: React.ReactNode = "-";

                                if (key === "download" && titleId === "EXTENSION_LIST") {
                                    displayValue = value ? (
                                        <FormattedMessage id="DOWNLOADED" />
                                    ) : (
                                        <FormattedMessage id="NOT_DOWNLOADED" />
                                    );
                                } else if (Array.isArray(value)) {
                                    displayValue = value
                                        .map((item) =>
                                            typeof item === "string"
                                                ? item.charAt(0).toUpperCase() + item.slice(1)
                                                : item
                                        )
                                        .join(", ");
                                } else if (typeof value === "object" && value !== null) {
                                    displayValue = Object.values(value).join(", ");
                                } else if (typeof value === "string") {
                                    displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                                } else if (typeof value === "boolean") {
                                    displayValue = <FormattedMessage id={value ? "YES" : "NO"} />;
                                } else if (typeof value === "number") {
                                    displayValue = String(value);
                                }

                                return (
                                    <tr key={key}>
                                        <td className="text-gray-600 font-normal whitespace-nowrap">
                                            <FormattedMessage id={labelKey} defaultMessage={labelKey} />
                                        </td>
                                        <td className="text-gray-800 font-normal">{displayValue}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="container-fixed max-w-[auto] flex flex-col p-10 overflow-hidden [&>button]:hidden">
                    <DialogHeader className="p-0 border-0">
                        <DialogTitle></DialogTitle>
                        <DialogDescription></DialogDescription>
                        <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5">
                            <div className="flex flex-col justify-center gap-2">
                                <h1 className="text-xl font-semibold leading-none text-gray-900"><FormattedMessage id="MEMBERSHIP_DEAILS" /></h1>
                            </div>
                            <button className="btn btn-sm btn-light" onClick={onOpenChange}>
                                <FormattedMessage id="CLOSE" />
                            </button>
                        </div>
                    </DialogHeader>
                    <DialogBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-7" ref={parentRef}>
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
                            {
                                details ?
                                    <>
                                        <div className="col-span-1">
                                            <div className="flex flex-col gap-5 lg:gap-7.5 sticky top-0">
                                                <div className="card min-w-full">
                                                    <div className="card-header">
                                                        <h3 className="card-title"><FormattedMessage id="BASIC_DETAILS" /></h3>
                                                    </div>
                                                    <div className="card-table scrollable-x-auto pb-3">
                                                        {
                                                            details?.data ?
                                                                <div className="flex items-baseline flex-wrap lg:flex-nowrap gap-2.5 ">
                                                                    <table className="table align-middle text-sm text-gray-500">
                                                                        <tbody>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="TITLE" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.data?.membership?.title ?? "-"}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="DESCRIPTION" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.data?.membership?.description ?? "-"}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="VALIDITY" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.data?.membership?.subscription_validity ?? "-"}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="PRICE" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    <PriceCell value={details?.data?.membership?.price ?? 0} />
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="CREATED" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.data?.created_at ? formatIsoDate(details?.data?.created_at) : "-"}
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="text-gray-600 font-normal"><FormattedMessage id="EXPIRED_AT" /></td>
                                                                                <td className="text-gray-800 font-normal">
                                                                                    {details?.data?.expired_at ? formatIsoDate(details?.data?.expired_at) : "-"}
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                                : <div className="card-header border-[0px]"><span className='text-gray-800 font-normal'><FormattedMessage id="NOT_FOUND_MEMBERSHIP_BASIC_DETAIL" /></span></div>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7.5">
                                                <InfoCard titleId="ELEMENTS_LIST" data={details?.element_info} />
                                                <InfoCard titleId="EXTENSION_LIST" data={details?.extension_info} />
                                                <InfoCard titleId="T3HEADLESS" data={details?.t3headless_config} />
                                                <InfoCard titleId="T3THEMES" data={details?.t3themes_config} />
                                            </div>
                                        </div>
                                    </>
                                    :
                                    <div className="col-span-1">
                                        <div className="flex flex-col gap-5 lg:gap-7.5">
                                            <div className="card min-w-full">
                                                <div className="card-header border-[0px]">
                                                    <span className='text-gray-800 font-normal'><FormattedMessage id="NOT_FOUND_MEMBERSHIP_DETAILS" /></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                            }
                        </div>
                    </DialogBody>
                </DialogContent >
            </Dialog>
        </>
    );
};

export { MembershipView };
